import { useUnit } from 'effector-react';
import { Languages, Monitor, Moon, Settings, Sun } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  $locale,
  $theme,
  localeChanged,
  THEME_PREFERENCES,
  themeChanged,
  type ThemePreference,
} from '../../entities/preferences';
import { isLocale, LOCALES, type Locale } from '../../shared/lib/locale';
import {
  IconButton,
  Menu,
  MenuContent,
  MenuLabel,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
  Tooltip,
} from '../../shared/ui';

const THEME_LABEL_KEY = {
  dark: 'settings.themeDark',
  light: 'settings.themeLight',
  system: 'settings.themeSystem',
} as const satisfies Record<ThemePreference, string>;

const THEME_ICON: Record<ThemePreference, ReactNode> = {
  dark: <Moon />,
  light: <Sun />,
  system: <Monitor />,
};

const LOCALE_LABEL_KEY = {
  en: 'settings.languageEn',
  bg: 'settings.languageBg',
} as const satisfies Record<Locale, string>;

function isThemePreference(value: string): value is ThemePreference {
  return (THEME_PREFERENCES as readonly string[]).includes(value);
}

export function PreferencesMenu() {
  const { t } = useTranslation();
  const [theme, locale, onTheme, onLocale] = useUnit([
    $theme,
    $locale,
    themeChanged,
    localeChanged,
  ]);

  return (
    <Menu>
      <Tooltip content={t('settings.title')}>
        <MenuTrigger asChild>
          <IconButton label={t('settings.title')}>
            <Settings />
          </IconButton>
        </MenuTrigger>
      </Tooltip>
      <MenuContent className="min-w-52">
        <MenuLabel>{t('settings.theme')}</MenuLabel>
        <MenuRadioGroup
          value={theme}
          onValueChange={(value) => {
            if (isThemePreference(value)) onTheme(value);
          }}
        >
          {THEME_PREFERENCES.map((option) => (
            <MenuRadioItem key={option} value={option}>
              {THEME_ICON[option]}
              {t(THEME_LABEL_KEY[option])}
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
        <MenuSeparator />
        <MenuLabel>{t('settings.language')}</MenuLabel>
        <MenuRadioGroup
          value={locale}
          onValueChange={(value) => {
            if (isLocale(value)) onLocale(value);
          }}
        >
          {LOCALES.map((option) => (
            <MenuRadioItem key={option} value={option} lang={option}>
              <Languages />
              {t(LOCALE_LABEL_KEY[option])}
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
      </MenuContent>
    </Menu>
  );
}
